import ExcelJS, { Anchor } from 'exceljs';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';

import { MenuIdIndividualMenuInternalMenuItemGroupToBeMapped } from '../../interfaces';

/**
 * This function fetches images across all imi groups and stores them as an image id returned by the ExcelJS library
 * These images will then be referenced using their image ids when inserting into their respective cells
 * @param arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped - Array of individual menu imi group to be mapped
 * @param workbook - workbook object by ExcelJS
 * @param propertyMenuItemMappingDownloadController - React reference that stores the AbortController object that signals the outgoing Axios requests to stop
 * @returns mapArrSpreadSheetImageIdByGroupId - Map of arrSpreadSheetImageId by groupId
 */
const getMapArrSpreadSheetImageIdByGroupId = async (
  arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped: Array<MenuIdIndividualMenuInternalMenuItemGroupToBeMapped>,
  workbook: ExcelJS.Workbook,
  propertyMenuItemMappingDownloadController: React.RefObject<null | AbortController>
) => {
  const mapArrSpreadSheetImageIdByGroupId: Map<string, Array<number>> = new Map();

  await Promise.all(
    arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped.map(
      async (menuIdIndividualMenuInternalMenuItemGroupToBeMapped) => {
        const { arrIndividualMenuInternalMenuItemGroup } =
          menuIdIndividualMenuInternalMenuItemGroupToBeMapped;
        await Promise.all(
          arrIndividualMenuInternalMenuItemGroup.map(
            async (individualMenuInternalMenuItemGroup) => {
              const { arrInternalMenuItemGroup } = individualMenuInternalMenuItemGroup;
              await Promise.all(
                arrInternalMenuItemGroup.map(async (internalMenuItemGroup) => {
                  const { groupId, arrImageURL } = internalMenuItemGroup;
                  const arrSpreadsheetImageId = await Promise.all(
                    arrImageURL.map(async (imageURL) => {
                      const response = await fetch(imageURL, {
                        signal: propertyMenuItemMappingDownloadController?.current?.signal,
                      });
                      const arrayBuffer = await response.arrayBuffer();
                      const imageId = workbook.addImage({
                        buffer: arrayBuffer,
                        extension: 'jpeg',
                      });
                      return imageId;
                    })
                  );
                  mapArrSpreadSheetImageIdByGroupId.set(groupId, arrSpreadsheetImageId);
                })
              );
            }
          )
        );
      }
    )
  );

  return mapArrSpreadSheetImageIdByGroupId;
};

/**
 * This function generates the spreadsheet given the backend menu item mapping response
 * @param arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped - Array of individual menu imi group to be mapped
 * @param mapArrLocationServiceStringByMenuId - Map of arrLocationServiceString by menu id. This is used for tab name
 * @param propertyName - Name of property. This is used for file name
 * @param propertyMenuItemMappingDownloadController - React reference that stores the AbortController object that signals the outgoing Axios requests to stop
 * @param isPropertyMenuItemMappingDownloadCancelledRef - React reference that stores the boolean of whether the download is to be cancelled
 */
const downloadPropertyMenuItemMappingSpreadsheet = async (
  arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped: Array<MenuIdIndividualMenuInternalMenuItemGroupToBeMapped>,
  mapArrLocationServiceStringByMenuId: Map<number, Array<string>>,
  propertyName: string,
  propertyMenuItemMappingDownloadController: React.RefObject<null | AbortController>,
  isPropertyMenuItemMappingDownloadCancelledRef: React.MutableRefObject<boolean>
) => {
  const workbook = new ExcelJS.Workbook();

  // spreadSheetImageId is stored first before assigning a cell
  // While images can be directly inserted below, due to their async fetch nature it can cause unncessary waiting
  // or wrong row insertion order due to varied waiting time for image fetch, hence they are all fetched in advance
  const mapArrSpreadSheetImageIdByGroupId = await getMapArrSpreadSheetImageIdByGroupId(
    arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped,
    workbook,
    propertyMenuItemMappingDownloadController
  );

  arrMenuIdIndividualMenuInternalMenuItemGroupToBeMapped.forEach(
    (menuIdIndividualMenuInternalMenuItemGroupToBeMapped) => {
      if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
      const { menuId, arrIndividualMenuInternalMenuItemGroup } =
        menuIdIndividualMenuInternalMenuItemGroupToBeMapped;
      // Shorten tab name if more than 31 characters as excel has a char limit of 31 by default
      let tabName = mapArrLocationServiceStringByMenuId.get(menuId)!.join('_');
      if (tabName.length > 31) {
        tabName = `${tabName.slice(0, 28)}...`;
      }
      const worksheet = workbook.addWorksheet(tabName);
      worksheet.views = [{ state: 'frozen', xSplit: 4, ySplit: 1 }];
      worksheet.columns = [
        { header: 'Id', key: 'id', width: 10 },
        {
          header: 'Menu Item Name*',
          key: 'menuItemName',
          width: 30,
        },
        { header: 'Station*', key: 'station', width: 30, font: { color: { argb: 'ffff0000' } } },
        {
          header: 'Cost per Kilogram*',
          key: 'costPerKilogram',
          width: 30,
          font: { color: { argb: 'ffff0000' } },
        },
        { header: 'Image 1', key: 'imageOne', width: 26 },
        { header: 'Image 2', key: 'imageTwo', width: 26 },
        { header: 'Image 3', key: 'imageThree', width: 26 },
        { header: 'Image 4', key: 'imageFour', width: 26 },
        { header: 'Image 5', key: 'imageFive', width: 26 },
        { header: 'Image 6', key: 'imageSix', width: 26 },
        { header: 'Image 7', key: 'imageSeven', width: 26 },
        { header: 'Image 8', key: 'imageEight', width: 26 },
        { header: 'Image 9', key: 'imageNine', width: 26 },
        { header: 'Image 10', key: 'imageTen', width: 26 },
      ];
      arrIndividualMenuInternalMenuItemGroup.forEach((individualMenuInternalMenuItemGroup) => {
        if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
        const { startDate, expiryDate, arrInternalMenuItemGroup } =
          individualMenuInternalMenuItemGroup;
        const formattedStartDate = DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat(
          'dd LLL yyyy'
        );
        const formattedEndDate = expiryDate
          ? DateTime.fromFormat(expiryDate, 'yyyy-MM-dd').toFormat('dd LLL yyyy')
          : 'CURRENT';
        const dateRangeString = `${formattedStartDate} ~ ${formattedEndDate}`;
        const dateRow = worksheet.addRow({
          id: dateRangeString,
        });
        dateRow.getCell(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ff949494' },
        };
        dateRow.getCell(1).alignment = { horizontal: 'left' };
        // mergeCell values: start row, start column, end row, end column
        worksheet.mergeCells(dateRow.number, 1, dateRow.number, 14);
        arrInternalMenuItemGroup.forEach((internalMenuItemGroup) => {
          const { groupId, internalMenuItemIdWasteId } = internalMenuItemGroup;
          const arrWasteId = internalMenuItemIdWasteId.map(({ arrWasteId }) => arrWasteId).flat();
          const currentRow = worksheet.addRow({
            id: groupId,
            imageOne: arrWasteId[0],
            imageTwo: arrWasteId[1],
            imageThree: arrWasteId[2],
            imageFour: arrWasteId[3],
            imageFive: arrWasteId[4],
            imageSix: arrWasteId[5],
            imageSeven: arrWasteId[6],
            imageEight: arrWasteId[7],
            imageNine: arrWasteId[8],
            imageTen: arrWasteId[9],
          });
          currentRow.height = 155;
          // wasteIds are 'hidden' behind the waste images for mapping imi history creation later during spreadsheet consumption
          currentRow.eachCell((cell) => {
            // cell.col can be 'string' or 'number' by library default - hence checked first before doing comparison
            if (typeof cell.col === 'number' && cell.col >= 4 && cell.col <= 14) {
              cell.alignment = { horizontal: 'left', vertical: 'top' };
            }
          });
          const arrSpreadsheetImageId = mapArrSpreadSheetImageIdByGroupId.get(groupId)!;
          arrSpreadsheetImageId.forEach((spreadsheetImageId, spreadsheetImageIndex) => {
            worksheet.addImage(spreadsheetImageId, {
              tl: { col: spreadsheetImageIndex + 4, row: currentRow.number - 1 } as Anchor,
              br: { col: spreadsheetImageIndex + 5, row: currentRow.number } as Anchor,
              // Below editAs set to anchor the image to the cell such that when users delete the row, the image will
              // be deleted together with the cells.
              editAs: 'twoCell',
            });
            // Due to addition of images above, an extra row is created automatically in order to anchor the
            // bottom-right (br) of the image. The below splicing removes the additional row created.
            worksheet.spliceRows(currentRow.number + 1, 1);
          });
        });
      });

      if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
      // Apply styles for specific cells (i.e. first col and first row bolded)
      worksheet.getRow(1).eachCell((cell) => {
        // cell.col can be 'string' or 'number' by library default - hence checked first before doing comparison
        if (typeof cell.col === 'number' && cell.col >= 2 && cell.col <= 4) {
          cell.font = {
            bold: true,
            color: { argb: 'ffff0000' },
          };
        } else {
          cell.font = { bold: true };
        }
        cell.alignment = { horizontal: 'center' };
      });
      worksheet.getColumn(1).eachCell((cell) => {
        cell.font = { bold: true };
      });
    }
  );

  if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
  // Save the workbook as an Excel file
  const buffer = await workbook.xlsx.writeBuffer();
  if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  if (isPropertyMenuItemMappingDownloadCancelledRef.current) return;
  saveAs(blob, `${propertyName}_MenuItemMapping.xlsx`);
};

export default downloadPropertyMenuItemMappingSpreadsheet;
